import { put, call, select } from 'redux-saga/effects';

import { NIMBLY_API_REPORT_SUBMIT } from '../../../constants/api';
import { submitApiReportAsync, SubmitReportAPI } from './submitReport.action';
import RootNavigation from '../../../RootNavigation';
import { logErrorWithContext, addBreadcrumb } from '../../../utils/sentry/sentrySetup';
import { track } from '../../../utils/analytics';
import API from '../../helpers/api';

type LiteAttachmentMetadata = {
  uri: string;
  firebaseStoragePath?: string;
  fileName?: string;
  createdAt?: string;
};

const VALIDATION_ERROR_PREFIX = 'submitReport validation:';

const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

const getValidApiReportIDCandidate = (candidate: unknown): string | undefined => {
  if (!isNonEmptyString(candidate)) {
    return undefined;
  }

  return candidate.trim();
};

const isRemoteAttachmentUri = (value: unknown): value is string =>
  isNonEmptyString(value) &&
  (value.startsWith('report/') || value.startsWith('http://') || value.startsWith('https://'));

const createSubmitValidationError = (message: string) => new Error(`${VALIDATION_ERROR_PREFIX} ${message}`);

const isSubmitValidationError = (error: unknown): boolean =>
  error instanceof Error && error.message.startsWith(VALIDATION_ERROR_PREFIX);

const getAttachmentMetadataKey = (uri: string): string => {
  const fileName = uri.split('/').pop()?.split('?')[0] || uri;
  const extensionIndex = fileName.lastIndexOf('.');

  return extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
};

const createAttachmentMetadataEntry = (uri: string, createdAt?: string): LiteAttachmentMetadata => {
  const fileName = uri.split('/').pop()?.split('?')[0] || uri;
  const metadata: LiteAttachmentMetadata = {
    uri,
    fileName,
    firebaseStoragePath: uri
  };

  if (createdAt) {
    metadata.createdAt = createdAt;
  }

  return metadata;
};

const findFirstUnresolvedAttachmentInList = (attachments?: unknown[]): unknown => {
  if (!Array.isArray(attachments)) {
    return undefined;
  }

  return attachments.find(attachment => !!attachment && !isRemoteAttachmentUri(attachment));
};

const findFirstUnresolvedAttachmentInQuestion = (question: any): unknown => {
  const directAttachment =
    findFirstUnresolvedAttachmentInList(question?.photos) ||
    findFirstUnresolvedAttachmentInList(question?.videos) ||
    findFirstUnresolvedAttachmentInList(question?.documents);

  if (directAttachment) {
    return directAttachment;
  }

  const conditionalQuestions = question?.conditionalQuestion?.conditions;
  if (conditionalQuestions && typeof conditionalQuestions === 'object') {
    for (const conditionalQuestionGroup of Object.values(conditionalQuestions) as any[]) {
      if (!Array.isArray(conditionalQuestionGroup)) {
        continue;
      }

      for (const conditionalQuestion of conditionalQuestionGroup) {
        const conditionalAttachment = findFirstUnresolvedAttachmentInQuestion(conditionalQuestion);
        if (conditionalAttachment) {
          return conditionalAttachment;
        }
      }
    }
  }

  return undefined;
};

const findFirstUnresolvedAttachmentInReport = (report: any): unknown => {
  const questions = Array.isArray(report?.questions) ? report.questions : [];
  for (const question of questions) {
    const unresolvedQuestionAttachment = findFirstUnresolvedAttachmentInQuestion(question);
    if (unresolvedQuestionAttachment) {
      return unresolvedQuestionAttachment;
    }
  }

  const unresolvedSignature = (report?.signatures || []).find(
    (signature: any) => !!signature?.path && !isRemoteAttachmentUri(signature.path)
  );
  if (unresolvedSignature?.path) {
    return unresolvedSignature.path;
  }

  const unresolvedSelfieSignature = (report?.selfieSignatures || []).find(
    (signature: any) => !!signature?.path && !isRemoteAttachmentUri(signature.path)
  );
  if (unresolvedSelfieSignature?.path) {
    return unresolvedSelfieSignature.path;
  }

  return undefined;
};

const collectAttachmentMetadataFromQuestion = (
  question: any,
  attachmentMetadata: Record<string, LiteAttachmentMetadata>,
  createdAt?: string
) => {
  const registerAttachments = (attachments?: unknown[]) => {
    if (!Array.isArray(attachments)) {
      return;
    }

    attachments.forEach(attachment => {
      if (!isRemoteAttachmentUri(attachment)) {
        return;
      }

      const key = getAttachmentMetadataKey(attachment);
      if (!key || attachmentMetadata[key]) {
        return;
      }

      attachmentMetadata[key] = createAttachmentMetadataEntry(attachment, createdAt);
    });
  };

  registerAttachments(question?.photos);
  registerAttachments(question?.videos);
  registerAttachments(question?.documents);

  const conditionalQuestions = question?.conditionalQuestion?.conditions;
  if (conditionalQuestions && typeof conditionalQuestions === 'object') {
    Object.values(conditionalQuestions).forEach((conditionalQuestionGroup: any) => {
      if (!Array.isArray(conditionalQuestionGroup)) {
        return;
      }

      conditionalQuestionGroup.forEach((conditionalQuestion: any) => {
        collectAttachmentMetadataFromQuestion(conditionalQuestion, attachmentMetadata, createdAt);
      });
    });
  }
};

const buildAttachmentMetadata = (questions: any[], createdAt?: string): Record<string, LiteAttachmentMetadata> => {
  const attachmentMetadata: Record<string, LiteAttachmentMetadata> = {};

  questions.forEach(question => {
    collectAttachmentMetadataFromQuestion(question, attachmentMetadata, createdAt);
  });

  return attachmentMetadata;
};

const sanitizeQuestionForLiteSubmit = (question: any): any => {
  if (!question || typeof question !== 'object') {
    return question;
  }

  const { downloadedImage: _downloadedImage, conditionalQuestion, ...sanitizedQuestion } = question;

  if (conditionalQuestion && typeof conditionalQuestion === 'object') {
    sanitizedQuestion.conditionalQuestion = {
      ...conditionalQuestion,
      conditions: Object.entries(conditionalQuestion.conditions || {}).reduce((acc, [answerKey, questionGroup]) => {
        acc[answerKey] = Array.isArray(questionGroup)
          ? questionGroup.map(conditionalQuestionItem => sanitizeQuestionForLiteSubmit(conditionalQuestionItem))
          : questionGroup;

        return acc;
      }, {} as Record<string, any>)
    };
  }

  return sanitizedQuestion;
};

const resolveLiteApiReportID = (
  reportId: string,
  cachedReport: any,
  report: any,
  payloadApiReportID?: string
): string => {
  const payloadCandidate = getValidApiReportIDCandidate(payloadApiReportID);
  if (payloadCandidate) {
    return payloadCandidate;
  }

  const cachedApiReportID = getValidApiReportIDCandidate(cachedReport?.apiReportID);
  if (cachedApiReportID) {
    return cachedApiReportID;
  }

  const reportApiReportID = getValidApiReportIDCandidate(report?.apiReportID);
  if (reportApiReportID) {
    return reportApiReportID;
  }

  if (!isNonEmptyString(reportId)) {
    throw createSubmitValidationError('apiReportID is missing for lite submit');
  }

  return reportId.trim();
};

function buildLiteSubmitPayload(report: any) {
  const sanitizedQuestions = Array.isArray(report?.questions)
    ? report.questions.map((question: any) => sanitizeQuestionForLiteSubmit(question))
    : [];
  const attachmentCreatedAt =
    report?.datetimeUpdated || report?.datetimeSubmitted || report?.datetimeOut || report?.datetimeIn;

  return {
    questions: sanitizedQuestions,
    signatures: report.signatures?.length ? report.signatures : [],
    selfieSignatures: report.selfieSignatures?.length ? report.selfieSignatures : [],
    emailTargets: report.emailTargets?.length ? report.emailTargets : [],
    submittedAt: report.datetimeSubmitted || new Date().toISOString(),
    checkOutAt: report.datetimeOut || new Date().toISOString(),
    attachmentMetadata: buildAttachmentMetadata(sanitizedQuestions, attachmentCreatedAt),
    ...(report.datetimeIn ? { checkInAt: report.datetimeIn } : {})
  };
}

export function* submitReportSaga(action: { payload: SubmitReportAPI }): Generator {
  const reportId = action?.payload?.reportId;
  const siteId = action?.payload?.siteId;
  const report = action?.payload?.report as any;

  try {
    if (!siteId) {
      yield put(submitApiReportAsync.failure('siteId is missing'));
      action.payload.callback.reject();
      return;
    }

    if (!isNonEmptyString(reportId)) {
      throw createSubmitValidationError('reportId is missing');
    }

    const unresolvedAttachment = findFirstUnresolvedAttachmentInReport(report);
    if (unresolvedAttachment) {
      throw createSubmitValidationError(`unresolved attachment reference: ${String(unresolvedAttachment)}`);
    }

    const state = (yield select()) as any;
    const cachedReport = state.reportcache?.reports?.[siteId]?.[reportId];
    const apiReportID = resolveLiteApiReportID(reportId, cachedReport, report, action.payload.apiReportID);

    const url = `${NIMBLY_API_REPORT_SUBMIT}/${siteId}/${apiReportID}/submit`;
    const payload = buildLiteSubmitPayload(report);

    addBreadcrumb('submitReport', 'Lite submit started', 'info', {
      siteId,
      reportId,
      apiReportID,
      attachmentMetadataCount: Object.keys(payload.attachmentMetadata || {}).length
    });

    const api = (yield call(API.withToken)) as API;
    yield call([api, api.postJSON], url, payload);

    addBreadcrumb('submitReport', 'Lite submit succeeded', 'info', {
      siteId,
      reportId,
      apiReportID
    });

    track('report_submitted', { reportId, siteId, apiReportID });
    yield put(submitApiReportAsync.success(true));
    action.payload.callback.resolve();
  } catch (err) {
    const normalizedError = err instanceof Error ? err : new Error(String(err));

    track('report_submit_failed', { reportId: reportId ?? '', siteId: siteId ?? '', error: normalizedError.message });

    logErrorWithContext(normalizedError, 'saga_submit_report_error', {
      saga: 'submitReportSaga',
      component: 'submitReport.actionSaga.ts',
      reportId,
      siteId,
      validationError: isSubmitValidationError(normalizedError)
    });

    if (!isSubmitValidationError(normalizedError)) {
      RootNavigation.navigate('HomeTab', {
        screen: 'Home'
      });
    }

    yield put(submitApiReportAsync.failure(normalizedError.message));
    action.payload.callback.reject();
  }
}
